import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, inject } from '@angular/core';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatDivider, MatListItem, MatNavList } from '@angular/material/list';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, RouterEvent, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { BehaviorSubject, filter, Observable, Subscription } from 'rxjs';
import { InternationalizationService } from '@soff/shared-ui';
import { environment } from '../environments/environment';
import * as versioning from './shared/changelog/version';
import { BannerComponent } from './shared/components/banner/banner.component';
import { BreadCrumbComponent } from './shared/components/breadcrumb/breadcrumb.component';
import { imprintLinks } from './shared/components/navbar/imprint';
import { SideNavigationItem, SideNavigationItems } from './shared/components/navbar/side-navbar.source';
import { TopNavbarComponent } from './shared/components/navbar/top-navbar/top-navbar.component';
import { AccessGuard } from './shared/guard/access.guard';
import { BreadCrumbData } from './shared/models/layout.model';
import { ServiceChargeService } from './shared/services/account/service-charge.service';
import * as RootState from './shared/state';
import { BaseActions } from './shared/state/base';
import { LayoutActions, LayoutReducer, LayoutSelectors } from './shared/state/layout';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    CommonModule,
    BreadCrumbComponent,
    TranslateModule,
    MatListItem,
    RouterLinkActive,
    MatIcon,
    MatSidenavModule,
    MatNavList,
    TopNavbarComponent,
    MatDivider,
    BannerComponent,
  ],
  providers: [InternationalizationService, ServiceChargeService, MatDivider],
})
export class AppComponent implements OnInit, OnDestroy {
  private internationalizationService = inject(InternationalizationService);
  private store = inject<Store<RootState.State>>(Store);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private _router = inject(Router);
  private layoutStore = inject<Store<LayoutReducer.State>>(Store);
  private change = inject(ChangeDetectorRef);
  accessGuard = inject(AccessGuard);
  authStateService = inject(OktaAuthStateService);

  @ViewChild('soffSideNavigation') public drawer: MatDrawer;
  public sideNavOpened$: Observable<boolean> = this.store.select(LayoutSelectors.getSideNavOpened);
  public sideNavigationItems: SideNavigationItem[] = SideNavigationItems;

  public soffFEVersion: string = versioning.soffFEVersion;
  public bannerClosed = false;

  public currentURL: string;

  isExpanded = false;

  private isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoggedIn$: Observable<boolean> = this.isLoggedInSubject.asObservable();

  public breadCrumbData$: Observable<BreadCrumbData> = this.layoutStore.select(LayoutSelectors.getBreadCrumbData);
  public breadCrumbData: BreadCrumbData;
  public breadCrumbSub: Subscription;

  public isLoginPage = false;
  public isOnIntroPage = false;
  public isOnLegalPage = false;
  public downloadLink = '';
  public maintenanceInfoLink = '';

  checkAuth(): void {
    this.authStateService.authState$.pipe(filter((s: AuthState) => !!s)).subscribe((s: AuthState) => {
      if (s?.isAuthenticated) {
        this.store.dispatch(new BaseActions.RehydrateApplicationState());
        this.downloadLink = environment.apiBaseUrl + '/frontend/static-files/ReleaseNotes/Planning_Accuracy.pdf?_jwt=' + s?.accessToken?.accessToken;
        this.maintenanceInfoLink =
          environment.apiBaseUrl +
          '/frontend/static-files/Maintenance_Windows/SOFF_Maintenance _notice_September 12_13_2026.pdf?_jwt=' +
          s?.accessToken?.accessToken;
      }
      this.isLoggedInSubject.next(s.isAuthenticated);
    });
  }

  setViewContextByUrl(url: string): void {
    if (url.startsWith('/intro')) {
      this.isOnIntroPage = true;
      this.isLoginPage = false;
      this.isOnLegalPage = false;
    } else if (url.startsWith('/legal')) {
      this.isOnLegalPage = true;
      this.isLoginPage = false;
      this.isOnIntroPage = false;
    } else if (url.startsWith('/app/account/login')) {
      this.isLoginPage = true;
      this.isOnLegalPage = false;
      this.isOnIntroPage = false;
    } else {
      this.isLoginPage = false;
      this.isOnIntroPage = false;
      this.isOnLegalPage = false;
    }
  }

  setViewContext(val: RouterEvent): void {
    if (val instanceof NavigationStart) return this.setViewContextByUrl(val.url);
    else if (val instanceof NavigationEnd) return this.setViewContextByUrl(val.urlAfterRedirects);
    // fallback
    return;
  }

  ngOnInit() {
    this.checkAuth();

    document.querySelector('html')?.setAttribute('lang', this.internationalizationService.getUserLanguage());

    this.sideNavigationItems.forEach((item: SideNavigationItem) => {
      if (item.labelI18nKey === 'global.navigation.disclaimer') {
        switch (this.internationalizationService.getUserLanguage()) {
          case 'de':
            item.routerLink = '/app/disclaimer/de';
            break;
          case 'en':
            item.routerLink = '/app/disclaimer/en';
            break;
          case 'es':
            item.routerLink = '/app/disclaimer/es';
            break;
          case 'fr':
            item.routerLink = '/app/disclaimer/fr';
            break;
          case 'ru':
            item.routerLink = '/app/disclaimer/ru';
            break;
          case 'uk':
            item.routerLink = '/app/disclaimer/uk';
            break;
        }
      }

      if (item.labelI18nKey === 'global.navigation.privacy') {
        switch (this.internationalizationService.getUserLanguage()) {
          case 'de':
            item.routerLink = '/app/privacy/de';
            break;
          case 'en':
            item.routerLink = '/app/privacy/en';
            break;
          case 'es':
            item.routerLink = '/app/privacy/es';
            break;
          case 'fr':
            item.routerLink = '/app/privacy/fr';
            break;
          case 'ru':
            item.routerLink = '/app/privacy/ru';
            break;
          case 'uk':
            item.routerLink = '/app/privacy/uk';
            break;
        }
      }

      this.iconRegistry.addSvgIconInNamespace('base', item.icon, this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/sidenav/' + item.icon));
    });
    this.iconRegistry.addSvgIconInNamespace('base', 'imprint', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/sidenav/paragraph.svg'));

    this.breadCrumbSub = this.breadCrumbData$.subscribe(data => {
      this.breadCrumbData = data;
      this.change.detectChanges();
    });

    this._router.events.subscribe((val: any) => {
      this.setViewContext(val);
    });
  }

  showTermsOfUseBreadcrumb() {
    const targetURL = ['app'];
    this.layoutStore.dispatch(
      new LayoutActions.ShowBreadCrumb({
        targetLink: targetURL,
        targetNameI18n: 'TermsOfUse',
        resourceName: 'Service Office',
      })
    );
  }

  showPrivacyBreadcrumb() {
    const targetURL = ['app'];
    this.layoutStore.dispatch(
      new LayoutActions.ShowBreadCrumb({
        targetLink: targetURL,
        targetNameI18n: 'Privacy',
        resourceName: 'Service Office',
      })
    );
  }

  ngOnDestroy() {
    //    this.userProfile$.unsubscribe();
    this.breadCrumbSub?.unsubscribe();
  }

  getActionFromNavbar(event: any) {
    this.isExpanded = event;
  }

  openApp(app: any) {
    if (app.href) {
      this._router?.navigate([app.href]);
    } else if (app.link) {
      window.location.href = app.link;
    }
  }

  navigateToImprint(): void {
    let link: string = imprintLinks[this.internationalizationService.getUserLanguage()];
    if (!link) link = imprintLinks['en'];
    window.open(link, '_blank');
  }

  closeBanner(): void {
    this.bannerClosed = true;
  }
}
